import { Link } from "react-router-dom";
import {
  AlertCircle,
  Calculator,
  Phone,
  HelpCircle,
  FileSearch,
  MessageCircle,
  Mail,
  ArrowRight,
  ChevronDown,
} from "lucide-react";
import { useState } from "react";
import { PolicyPageShell } from "@/components/PolicyHeader";

const SERVICES = [
  {
    to: "/grievance",
    icon: AlertCircle,
    title: "Raise a Complaint",
    desc: "7-tier escalation with SLA-backed resolution. Get a ticket in 24 hours.",
    testId: "cs-tile-grievance",
  },
  {
    to: "/#emi",
    icon: Calculator,
    title: "EMI Calculator",
    desc: "Plan your next loan — ₹50,000 to ₹50 Cr range, interactive sliders.",
    testId: "cs-tile-emi",
  },
  {
    to: "/#contact",
    icon: Phone,
    title: "Contact Branch",
    desc: "Reach our nearest branch directly — phone, WhatsApp or email.",
    testId: "cs-tile-contact",
  },
  {
    to: "/policies",
    icon: FileSearch,
    title: "Policies & Charters",
    desc: "Customer Charter, Privacy Policy, Code of Conduct.",
    testId: "cs-tile-policies",
  },
];

const FAQS = [
  {
    q: "Do you give loans to people without a CIBIL score?",
    a: "Yes. Our flagship 'No-CIBIL Loan' product is designed exactly for first-time borrowers, self-employed without formal salary slips, and those rebuilding credit. Approval is based on income flow, references and our advisor's KYC report — not just a credit bureau score.",
  },
  {
    q: "How quickly are loans approved?",
    a: "Personal & Gold loans are processed quickly from document submission. Home & Business loans: 5-10 working days. Track your application status by calling 70169 75324 or via WhatsApp.",
  },
  {
    q: "What documents are required?",
    a: "KYC (Aadhaar + PAN), Income proof (3 months bank statement or salary slips OR ITR for self-employed), Address proof, Property docs for secured loans. Your assigned Advisor will share a checklist tailored to your product.",
  },
  {
    q: "Are there any hidden charges?",
    a: "No. All charges — processing fee, GST, documentation, prepayment — are disclosed in the sanction letter before disbursal. Our standard processing fee is 1-2%, and we waive it during festival offers (see /hot-offers).",
  },
  {
    q: "How do I become a DSA or Advisor partner?",
    a: "Apply via /apply/dsa or /apply/advisor. Joining fee is ₹999 (DSA) / ₹499 (Advisor). You get a 7-8 module training pack, ready leads, and commissions from 0.6% to 2% per closed loan.",
  },
  {
    q: "How do I track my complaint after submitting?",
    a: "You receive a ticket number (SF-CGM-XXXXXXXX) immediately. Quote it on any of our channels — WhatsApp 70169 75324, email sahyogpfir@gmail.com, or call the branch — to get an update within 24 hours.",
  },
];

export default function CustomerServices() {
  const [open, setOpen] = useState(0);

  return (
    <PolicyPageShell
      testId="customer-services-page"
      eyebrow="Customer Services"
      title={
        <>
          We are <em className="not-italic text-[#D4AF37]">Here</em>,{" "}
          <span className="font-script italic text-[#F0D788]">Always</span>
        </>
      }
      lead="One hub for everything you need post-application — calculators, complaint redressal, policies, contact and answers to the most common questions."
    >
      {/* Direct contact strip */}
      <section
        data-testid="cs-direct-contact"
        className="mb-10 rounded-2xl border border-[#D4AF37]/25 bg-white/[0.03] p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8"
      >
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] md:w-40 shrink-0">
          Direct Contact
        </div>
        <a
          href="tel:+917016975324"
          data-testid="cs-contact-phone"
          className="inline-flex items-center gap-2 text-white/85 hover:text-[#F0D788] text-sm"
        >
          <Phone size={14} className="text-[#D4AF37]" /> 70169 75324
        </a>
        <a
          href="https://wa.me/917016975324"
          target="_blank"
          rel="noreferrer"
          data-testid="cs-contact-whatsapp"
          className="inline-flex items-center gap-2 text-white/85 hover:text-[#F0D788] text-sm"
        >
          <MessageCircle size={14} className="text-emerald-400" /> WhatsApp 70169 75324
        </a>
        <a
          href="mailto:sahyogpfir@gmail.com"
          data-testid="cs-contact-email"
          className="inline-flex items-center gap-2 text-white/85 hover:text-[#F0D788] text-sm"
        >
          <Mail size={14} className="text-[#D4AF37]" /> sahyogpfir@gmail.com
        </a>
      </section>

      {/* Services grid */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
        {SERVICES.map((s) => (
          <Link
            key={s.to}
            to={s.to}
            data-testid={s.testId}
            className="group rounded-2xl p-6 border border-white/10 bg-white/[0.03] hover:border-[#D4AF37]/45 hover:-translate-y-1 transition-all"
          >
            <div className="w-12 h-12 rounded-xl grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#F0D788]">
              <s.icon size={22} />
            </div>
            <h3 className="mt-4 font-display text-lg text-white group-hover:text-[#F0D788]">
              {s.title}
            </h3>
            <p className="mt-1.5 text-white/55 text-xs leading-relaxed">{s.desc}</p>
            <div className="mt-4 text-[#F0D788] text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-1">
              Open <ArrowRight size={11} />
            </div>
          </Link>
        ))}
      </section>

      {/* Loan Status check */}
      <section className="mb-16 rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/[0.07] via-[#0A1F44] to-[#04102B] p-7 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">Loan Status Check</div>
          <h2 className="font-display text-2xl mt-2">Track your application in 5 to 6 working days</h2>
          <p className="mt-2 text-white/65 text-sm max-w-xl">
            Quote your application reference number on WhatsApp 70169 75324 — our customer-service desk will reply with the current status, pending docs and expected disbursal window.
          </p>
        </div>
        <a
          href="https://wa.me/917016975324?text=Hi%2C%20I%20want%20to%20check%20my%20loan%20application%20status."
          target="_blank"
          rel="noreferrer"
          data-testid="cs-track-whatsapp"
          className="px-6 py-3 rounded-full bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/40 text-emerald-200 text-sm font-semibold inline-flex items-center gap-2"
        >
          <MessageCircle size={14} /> Check on WhatsApp
        </a>
      </section>

      {/* FAQs */}
      <section>
        <div className="mb-7">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">Frequently Asked</div>
          <h2 className="font-display text-3xl mt-2 flex items-center gap-3">
            <HelpCircle size={26} className="text-[#F0D788]" /> Questions &amp; Answers
          </h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={f.q}
                data-testid={`cs-faq-${i + 1}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden"
              >
                <button
                  data-testid={`cs-faq-toggle-${i + 1}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 md:px-6 py-4 md:py-5 text-left"
                >
                  <span className="font-display text-base md:text-lg text-white">{f.q}</span>
                  <ChevronDown
                    size={18}
                    className={`text-[#D4AF37] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 md:px-6 pb-5 text-white/70 text-sm leading-relaxed border-t border-white/5 pt-4">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </PolicyPageShell>
  );
}
